import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart,
} from 'recharts';
import {
  TrendingUp, Sparkles, Rocket, GraduationCap, Home, Briefcase, PiggyBank, Wallet,
  Target, Brain, Sliders, Layers, Plus, X, Zap, AlertTriangle, CheckCircle2, Lightbulb, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { computeProjection, contextualInsights, fmtMoney, fmtFull, MILESTONE_AGES } from '../utils/futureOutlook';
import PatriciaPageGuide from '../components/PatriciaPageGuide';
import PageArrival from '../components/PageArrival';
import { useFPContext } from '../context/FuturePathContext';
import { AddToDashboardButton } from '../components/AddToDashboardButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_COL = {
  AL: 0.88, AK: 1.25, AZ: 1.02, AR: 0.86, CA: 1.40, CO: 1.10, CT: 1.20, DE: 1.02, FL: 1.03, GA: 0.93,
  HI: 1.75, ID: 0.96, IL: 1.00, IN: 0.90, IA: 0.90, KS: 0.88, KY: 0.90, LA: 0.92, ME: 1.08, MD: 1.18,
  MA: 1.32, MI: 0.92, MN: 1.00, MS: 0.85, MO: 0.89, MT: 1.00, NE: 0.92, NV: 1.04, NH: 1.12, NJ: 1.24,
  NM: 0.94, NY: 1.30, NC: 0.95, ND: 0.95, OH: 0.90, OK: 0.87, OR: 1.14, PA: 1.00, RI: 1.12, SC: 0.95,
  SD: 0.93, TN: 0.91, TX: 0.97, UT: 1.03, VT: 1.10, VA: 1.05, WA: 1.18, WV: 0.88, WI: 0.95, WY: 0.96, DC: 1.55,
};

// ── Small building blocks ──────────────────────────────────────────────────
const Glow = ({ children, className = '', glow = 'rgba(23,204,244,0.14)', testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl border border-white/10 bg-[#0a1830]/70 backdrop-blur-xl p-5 ${className}`}
    style={{ boxShadow: `0 0 34px ${glow}, 0 8px 32px rgba(0,0,0,0.35)` }}
  >
    {children}
  </div>
);

const Stat = ({ icon: Icon, label, value, sub, accent = 'text-cyan-300' }) => (
  <div className="rounded-xl bg-slate-900/50 border border-white/5 p-3.5">
    {Icon && <Icon className={`w-4 h-4 mb-1.5 ${accent}`} />}
    <p className="text-lg sm:text-xl font-bold text-white leading-tight">{value}</p>
    <p className="text-[11px] text-slate-300 leading-tight mt-0.5">{label}</p>
    {sub && <p className={`text-[10px] mt-0.5 ${accent}`}>{sub}</p>}
  </div>
);

function SliderRow({ label, value, min, max, step = 1, onChange, fmt = (v) => v, icon: Icon, testid }) {
  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-slate-300 flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5 text-cyan-400/80" />} {label}
        </span>
        <span className="text-xs font-semibold text-cyan-200">{fmt(value)}</span>
      </div>
      <Slider data-testid={testid} value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}

const chartAxis = { stroke: '#C6D4E8', fontSize: 11 };
const tipStyle = { background: '#0a1830', border: '1px solid #1e3a55', borderRadius: 10, color: '#fff', fontSize: 12 };


// The chart used to show "-$1M" with no explanation. This says, in plain words, what the line
// is, why it starts underwater, and the exact year it comes back up.
const ChartReadingGuide = ({ rows, savingLabel = 'Retirement' }) => {
  if (!rows?.length) return null;
  const low = rows.reduce((a, b) => (b.netWorth < a.netWorth ? b : a), rows[0]);
  const afterLow = rows.filter((r) => r.age >= low.age);
  const crossover = afterLow.find((r) => r.netWorth >= 0);
  const underwater = rows.filter((r) => r.netWorth < 0);
  const end = rows[rows.length - 1];
  const money = (n) => fmtFull(Math.round(Math.abs(n || 0)));
  const education = rows.filter((r) => r.inEducation);
  const shortRows = rows.filter((r) => (r.lifestyleGap || 0) > 0);
  const gapRow = shortRows[0] || null;
  const gapYears = shortRows.length;
  return (
    <div className="mt-4 rounded-xl border border-cyan-500/20 bg-slate-900/50 p-4" data-testid="chart-reading-guide">
      <p className="text-[11px] uppercase tracking-[0.16em] text-cyan-200 mb-2">How to read this chart</p>
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        <div>
          <p className="text-white font-semibold">Net Worth (the filled line)</p>
          <p className="text-slate-300 mt-0.5">Everything you own (cash, investments, {savingLabel.toLowerCase()}, home equity) minus everything you still owe.</p>
        </div>
        <div>
          <p className="text-white font-semibold">Investments</p>
          <p className="text-slate-300 mt-0.5">Money you've put into the market outside of {savingLabel.toLowerCase()}, growing at the return you set below.</p>
        </div>
        <div>
          <p className="text-white font-semibold">{savingLabel}</p>
          <p className="text-slate-300 mt-0.5">The long-term account plus any employer match. You can't spend it early without a penalty, so it's shown separately.</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/5 text-xs text-slate-300 space-y-1.5">
        {underwater.length > 0 ? (
          <>
            <p>
              <span className="text-rose-300 font-semibold">Below zero is debt, not lost money.</span>{' '}
              A negative number means what you owe is bigger than what you own yet. For most people that's
              education debt landing before the salary does. Your lowest point is
              <span className="text-white font-semibold"> −{money(low.netWorth)} at age {low.age}</span>
              {low.studentLoan ? <>, when {money(low.studentLoan)} of that is still student loans</> : null}.
            </p>
            <p>
              {crossover
                ? <>You cross back above zero at <span className="text-emerald-300 font-semibold">age {crossover.age}</span>, that's{' '}
                    {crossover.age - low.age} years of paying it down, and by age {end.age} the projection reaches
                    <span className="text-emerald-300 font-semibold"> {money(end.netWorth)}</span>.</>
                : <>On these settings the line never comes back above zero. That's the signal to change something,                     lower the borrowing, raise the destination pay, or pick a cheaper route.</>}
            </p>
            <p className="text-slate-300">
              Years spent below zero: {underwater.length} (ages {underwater[0].age}-{underwater[underwater.length - 1].age}).
            </p>
          </>
        ) : (
          <p>You never go below zero on these settings. What you owe stays smaller than what you own the whole way through.</p>
        )}
        {gapRow && (
          <p data-testid="chart-lifestyle-gap">
            <span className="text-amber-300 font-semibold">One thing the line is protecting you from:</span>{' '}
            the lifestyle you designed costs about {money(gapRow.lifestyleGap)} a year more than this career pays
            at age {gapRow.age}. We spend only what you'd actually have and show the shortfall here, rather than
            pretending you'd borrow it. {gapYears} of the working years are short on these settings.
          </p>
        )}
        {education.length > 0 && (
          <p className="text-slate-300">
            Ages {education[0].age}-{education[education.length - 1].age} are your education years. You earn nothing
            and we don't charge you adult living costs in them. Family, aid or the education cost itself carries that,
            so the line only moves with what you borrow.
          </p>
        )}
        <p className="text-slate-300">These are projections from your own inputs, not predictions. Change a dial and every number here moves.</p>
      </div>
    </div>
  );
};

export default function FutureFinancialOutlookPage() {
  const { getAuthHeader } = useAuth();
  const { phrase, traits } = useFPContext();
  const savingLabel = traits?.adult === false ? 'Long-term savings' : 'Retirement';
  const [profile, setProfile] = useState(null);
  const [market, setMarket] = useState({});
  const [sc, setSc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState([]); // scenario comparison
  const [coach, setCoach] = useState(null);
  const [coaching, setCoaching] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/future-outlook/inputs`, getAuthHeader()),
      axios.get(`${API}/future-outlook/scenarios`, getAuthHeader()).catch(() => ({ data: { scenarios: [] } })),
    ]).then(([inp, scn]) => {
      setProfile(inp.data.profile); setMarket(inp.data.market || {}); setSc(inp.data.scenario);
      const list = scn.data.scenarios || [];
      setSaved(list.map((d) => ({ id: d.id, name: d.name, scenario: d.scenario, career: d.scenario?.career, state: d.scenario?.state, ...(d.summary || {}) })));
    }).catch(() => toast.error('Could not load your financial outlook.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const proj = useMemo(() => (sc ? computeProjection(sc) : null), [sc]);
  const insights = useMemo(() => (sc && proj ? contextualInsights(sc, proj) : []), [sc, proj]);

  const set = (patch) => setSc((p) => ({ ...p, ...patch }));
  const setState = (code) => set({ state: code, col_multiplier: STATE_COL[code] || 1, monthly_living_cost: Math.round(3200 * (STATE_COL[code] || 1)), home_price: Math.round(340000 * (STATE_COL[code] || 1)) });

  const presets = [
    { label: 'Invest at 22', apply: () => set({ savings_rate: 20, investment_pct: 85 }) },
    { label: 'Community college first', apply: () => set({ education_cost: 45000, loan_amount: 20000, education_years: 4 }) },
    { label: 'Add a Master’s', apply: () => set({ education_years: (sc.education_years || 4) + 2, education_cost: (sc.education_cost || 0) + 60000, target_income: Math.round((sc.target_income || sc.salary_mid) * 1.18) }) },
    { label: 'Move to Texas', apply: () => setState('TX') },
    { label: 'Add a side business', apply: () => set({ side_income: 25000 }) },
    { label: 'Retire at 55', apply: () => set({ retirement_age: 55, savings_rate: 25 }) },
    { label: 'Buy a home at 30', apply: () => set({ housing: 'own' }) },
  ];

  const runCoach = async () => {
    setCoaching(true);
    try {
      const r = await axios.post(`${API}/future-outlook/coach`, { scenario: sc, summary: proj.summary, profile }, getAuthHeader());
      setCoach(r.data);
    } catch { toast.error('Coach is unavailable right now.'); }
    finally { setCoaching(false); }
  };

  const saveScenario = async () => {
    if (saved.length >= 6) { toast.info('You can keep up to 6 saved scenarios. Remove one first.'); return; }
    const summary = {
      pathway: profile?.pathway_label,
      retire: sc.retirement_age,
      lifetime: proj.summary.lifetimeEarnings,
      netWorth: proj.summary.netWorthAtRetirement,
      fiAge: proj.summary.financialIndependenceAge,
    };
    const name = (() => {
      const used = new Set(saved.map((s) => s.name));
      for (let i = 0; i < 26; i++) { const c = `Scenario ${String.fromCharCode(65 + i)}`; if (!used.has(c)) return c; }
      return `Scenario ${Date.now()}`;
    })();
    try {
      const r = await axios.post(`${API}/future-outlook/scenarios`, { name, scenario: sc, summary }, getAuthHeader());
      if (!r.data.success) { toast.info(r.data.message || 'Could not save.'); return; }
      const d = r.data.scenario;
      setSaved((s) => [...s, { id: d.id, name: d.name, scenario: d.scenario, career: d.scenario?.career, state: d.scenario?.state, ...(d.summary || {}) }]);
      toast.success('Scenario saved.');
    } catch { toast.error('Could not save scenario.'); }
  };

  const removeScenario = async (id) => {
    const prev = saved;
    setSaved((arr) => arr.filter((x) => x.id !== id));
    try { await axios.delete(`${API}/future-outlook/scenarios/${id}`, getAuthHeader()); }
    catch { setSaved(prev); toast.error('Could not remove scenario.'); }
  };

  const openScenario = (s) => {
    if (s.scenario) { setSc(s.scenario); toast.info(`Loaded ${s.name} into the simulator.`); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  if (loading) return <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="future_outlook" /></div><div className="flex justify-center py-24"><LoadingSpinner /></div></AppLayout>;
  if (!sc || !proj) return <AppLayout><div className="text-slate-300 py-24 text-center">Future Financial Outlook is available for students.</div></AppLayout>;

  const { summary, years, milestones } = proj;
  const chartData = years.filter((y) => y.age <= 70);
  const affordabilityNow = milestones[35]?.affordability ?? 0;

  if (!entered) return (
    <AppLayout>
      <PageArrival
        testid="financial-arrival"
        eyebrow="My Financial Future"
        title="See your money 40 years out."
        subtitle={`This is the same life, run forward. Your pay in ${sc.career}, what you'll owe, what you'll keep, and the year it stops being tight, then change one decision and watch the whole line move.`}
        accent="#2FE0A8"
        second="#17CCF4"
        ctaLabel="Open my financial future"
        narration={`This is My Financial Future. It takes what you've already told us, your career, your state, what your education costs, and runs it forward year by year. You'll see lifetime earnings, net worth at retirement, the age you could stop needing a paycheck, and what your money is really doing while you sleep. Then you change one thing: move state, invest earlier, add a side income, retire at fifty five. The whole picture moves with you. Nothing here is a guess about you. It's your own numbers, projected.`}
        facts={[
          { icon: Wallet, head: fmtMoney(summary.lifetimeEarnings), sub: 'Lifetime earnings on your current path' },
          { icon: TrendingUp, head: fmtMoney(summary.netWorthAtRetirement), sub: `Net worth by age ${summary.retirementAge}` },
          { icon: Rocket, head: summary.financialIndependenceAge ? `Age ${summary.financialIndependenceAge}` : 'Not yet reached', sub: 'When work becomes optional' },
        ]}
        modules={[
          { icon: Briefcase, label: 'Career snapshot', line: `Entry to peak pay in ${sc.career}, with growth, automation risk and remote reality.`, hue: '#17CCF4' },
          { icon: GraduationCap, label: 'Education investment', line: 'What the credential costs, what aid covers, the interest, and what it returns.', hue: '#a78bfa' },
          { icon: Target, label: 'Lifestyle alignment', line: 'Whether this path actually funds the life you designed, as a percentage.', hue: '#2FE0A8' },
          { icon: Sliders, label: 'Move the dials', line: `Savings rate, investing, state, housing, side income, ${phrase('long_term_saving', 'retirement')} age.`, hue: '#fbbf24' },
          { icon: Layers, label: 'Compare scenarios', line: 'Save up to six versions of your life and put them side by side.', hue: '#f472b6' },
          { icon: Brain, label: 'Coach read-out', line: 'A plain-English read on what your numbers say to do next.', hue: '#818cf8' },
        ]}
        footnote="Projections, not predictions. Wages grow at the rate your field actually grows, investments at long-run market averages, and every assumption is on screen where you can change it."
        onStart={() => { setEntered(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="min-h-full -m-2 sm:-m-4 p-2 sm:p-4" style={{ background: 'radial-gradient(1200px 600px at 80% -10%, rgba(124,58,237,0.10), transparent), radial-gradient(1000px 500px at 0% 10%, rgba(23,204,244,0.08), transparent)' }}>
        <div className="max-w-6xl mx-auto space-y-6 pb-12" data-testid="future-outlook-page">

          {/* Hero */}
          <div className="relative flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-cyan-300 font-semibold" style={{ textShadow: '0 0 14px rgba(23,204,244,0.6)' }}>FuturePath Journey · Financial Command Center</p>
              <h1 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-white via-cyan-100 to-violet-300 bg-clip-text text-transparent" style={{ filter: 'drop-shadow(0 0 24px rgba(124,58,237,0.28))' }}>
                Future Financial Outlook
              </h1>
              <p className="text-slate-300 text-sm mt-1 max-w-2xl">
                A living simulation of {profile?.name ? `${profile.name}'s` : 'your'} future, see how today&apos;s choices as a
                <span className="text-cyan-300 font-medium"> {sc.career}</span> shape your wealth over a lifetime.
              </p>
            </div>
            <AddToDashboardButton moduleId="lifetime_earnings" label="Pin to Dashboard" />
          </div>

          {/* Top summary strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Wallet} label="Lifetime Earnings" value={fmtMoney(summary.lifetimeEarnings)} accent="text-cyan-300" />
            <Stat icon={TrendingUp} label={traits?.adult === false ? 'Net Worth by 60s' : 'Net Worth @ Retirement'} value={fmtMoney(summary.netWorthAtRetirement)} sub={`Age ${summary.retirementAge}`} accent="text-emerald-300" />
            <Stat icon={Rocket} label="Financial Independence" value={summary.financialIndependenceAge ? `Age ${summary.financialIndependenceAge}` : '-'} accent="text-violet-300" />
            <Stat icon={PiggyBank} label={`${savingLabel} Saved`} value={fmtMoney(summary.retirementContributions)} sub={`+${fmtMoney(summary.employerMatch)} match`} accent="text-amber-300" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Career Snapshot */}
            <Glow testid="career-snapshot" glow="rgba(23,204,244,0.16)">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-cyan-200 mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Career Snapshot</h2>
              <p className="text-white font-bold text-lg leading-tight">{sc.career}</p>
              {profile?.personality_type && <p className="text-xs text-slate-300 mb-3">{profile.personality_type}</p>}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Stat label="Entry" value={fmtMoney(sc.salary_entry)} />
                <Stat label="Mid-Career" value={fmtMoney(sc.salary_mid)} />
                <Stat label="Senior" value={fmtMoney(sc.salary_senior)} />
                <Stat label="Peak" value={fmtMoney(sc.salary_peak)} />
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                {market.growth_rate != null && <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300">{market.growth_rate}% growth</span>}
                {market.automation_risk != null && <span className="px-2 py-1 rounded-full bg-amber-500/15 text-amber-300">{Math.round(market.automation_risk * 100)}% automation risk</span>}
                {market.remote_availability != null && <span className="px-2 py-1 rounded-full bg-cyan-500/15 text-cyan-300">{Math.round(market.remote_availability * 100)}% remote</span>}
                {market.outlook && <span className="px-2 py-1 rounded-full bg-violet-500/15 text-violet-300 capitalize">{market.outlook} outlook</span>}
              </div>
            </Glow>

            {/* Education Investment */}
            <Glow testid="education-investment" glow="rgba(124,58,237,0.16)">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-violet-200 mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Education Investment</h2>
              {profile?.favorite_college && <p className="text-xs text-slate-300 mb-2">Based on {profile.favorite_college}</p>}
              <div className="grid grid-cols-2 gap-2">
                <Stat label="Education Cost" value={fmtMoney(sc.education_cost)} accent="text-violet-300" />
                <Stat label="Scholarships + Grants" value={fmtMoney((sc.scholarships || 0) + (sc.grants || 0))} accent="text-emerald-300" />
                <Stat label="Loan Interest" value={fmtMoney(summary.studentLoanInterest)} accent="text-rose-300" />
                <Stat label="Monthly Payment" value={(() => { const P = Math.max(0, (sc.education_cost || 0) - (sc.scholarships || 0) - (sc.grants || 0)) * 0.85; const r = (sc.loan_rate || 0) / 100 / 12; const n = (sc.loan_term_years || 10) * 12; const pay = P <= 0 ? 0 : (r === 0 ? P / n : (P * r) / (1 - Math.pow(1 + r, -n))); return fmtFull(Math.round(pay)); })()} accent="text-amber-300" />
              </div>
              <p className="text-[11px] text-slate-300 mt-3">
                Return on education: every $1 of net cost projects to <span className="text-cyan-300 font-semibold">{fmtMoney(summary.lifetimeEarnings / Math.max(1, sc.education_cost - sc.scholarships - sc.grants)).replace('$','$')}</span> in lifetime earnings.
              </p>
            </Glow>

            {/* Lifestyle Alignment */}
            <Glow testid="lifestyle-alignment" glow="rgba(45,224,168,0.16)">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-200 mb-3 flex items-center gap-2"><Target className="w-4 h-4" /> Lifestyle Alignment</h2>
              <div className="flex items-center gap-4">
                <div className="relative w-24 h-24 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#1e3a55" strokeWidth="9" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="url(#alignGrad)" strokeWidth="9" strokeLinecap="round"
                      strokeDasharray={`${(affordabilityNow / 100) * 264} 264`} style={{ transition: 'stroke-dasharray 0.6s ease' }} />
                    <defs><linearGradient id="alignGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#17CCF4" /><stop offset="100%" stopColor="#2FE0A8" /></linearGradient></defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{affordabilityNow}%</span>
                  </div>
                </div>
                <div className="text-xs text-slate-300 space-y-1">
                  <p className="text-white font-semibold">Compatibility at 35</p>
                  <p>Your projected income vs. the lifestyle you designed.</p>
                  <p className="text-emerald-300">{affordabilityNow >= 80 ? 'Strong fit: your plan funds your goals.' : affordabilityNow >= 55 ? 'Close. Small tweaks close the gap.' : 'Gap. Raise income or adjust lifestyle.'}</p>
                </div>
              </div>
            </Glow>
          </div>

          {/* Financial Outlook Timeline */}
          <Glow testid="outlook-timeline" glow="rgba(23,204,244,0.12)">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-cyan-200 flex items-center gap-2" style={{ textShadow: '0 0 12px rgba(23,204,244,0.5)' }}><TrendingUp className="w-4 h-4" /> Lifetime Net Worth Timeline</h2>
              <span className="text-[11px] text-slate-300">Age {sc.current_age} → 70</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#17CCF4" stopOpacity={0.55} /><stop offset="100%" stopColor="#17CCF4" stopOpacity={0.02} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#16304a" vertical={false} />
                <XAxis dataKey="age" {...chartAxis} />
                <YAxis {...chartAxis} tickFormatter={fmtMoney} width={48} />
                <Tooltip contentStyle={tipStyle} formatter={(v, n) => [fmtFull(v), n]} labelFormatter={(a) => `Age ${a}`} />
                <Area type="monotone" dataKey="netWorth" name="Net Worth" stroke="#17CCF4" strokeWidth={2.5} fill="url(#nwGrad)" />
                <Line type="monotone" dataKey="investments" name="Investments" stroke="#2FE0A8" strokeWidth={1.6} dot={false} />
                <Line type="monotone" dataKey="retirement" name={savingLabel} stroke="#A78BFA" strokeWidth={1.6} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <ChartReadingGuide rows={chartData} savingLabel={savingLabel} />

            {/* Milestone strip */}
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-4">
              {MILESTONE_AGES.map((a) => (
                <div key={a} className="rounded-lg bg-slate-900/60 border border-white/5 p-2 text-center">
                  <p className="text-[10px] text-slate-300">Age {a}</p>
                  <p className="text-xs font-bold text-white">{fmtMoney(milestones[a]?.netWorth)}</p>
                  <p className="text-[9px] text-cyan-300/80">{fmtMoney(milestones[a]?.salary)}/yr</p>
                </div>
              ))}
            </div>
          </Glow>

          {/* Interactive Simulator */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Glow testid="career-simulator" glow="rgba(124,58,237,0.14)">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-violet-200 mb-2 flex items-center gap-2"><Sliders className="w-4 h-4" /> Career & Life Simulator</h2>
              <p className="text-[11px] text-slate-300 mb-2">Drag anything and your whole future recalculates instantly.</p>

              <div className="mb-3">
                <span className="text-xs text-slate-300 flex items-center gap-1.5 mb-1.5"><Home className="w-3.5 h-3.5 text-cyan-400/80" /> Location</span>
                <Select value={sc.state} onValueChange={setState}>
                  <SelectTrigger className="input-dark h-9 text-sm" data-testid="sim-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Object.keys(STATE_COL).map((code) => (
                      <SelectItem key={code} value={code}>{code} · COL {STATE_COL[code].toFixed(2)}×</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-3 flex gap-2">
                {['rent', 'own'].map((h) => (
                  <button key={h} onClick={() => set({ housing: h })} data-testid={`sim-housing-${h}`}
                    className={`flex-1 text-xs py-1.5 rounded-lg border capitalize transition-colors ${sc.housing === h ? 'border-cyan-400/50 bg-cyan-500/15 text-cyan-200' : 'border-slate-700 text-slate-300 hover:border-slate-600'}`}>
                    {h === 'own' ? 'Own a home' : 'Rent'}
                  </button>
                ))}
              </div>

              <SliderRow testid="sim-target-income" label="Target annual income" value={sc.target_income} min={30000} max={1000000} step={5000} onChange={(v) => set({ target_income: v })} fmt={fmtMoney} icon={Target} />
              <SliderRow testid="sim-education-years" label="Years of education" value={sc.education_years} min={0} max={8} step={1} onChange={(v) => set({ education_years: v })} fmt={(v) => `${v} yr`} icon={GraduationCap} />
              <SliderRow testid="sim-education-cost" label="Education cost" value={sc.education_cost} min={0} max={300000} step={5000} onChange={(v) => set({ education_cost: v })} fmt={fmtMoney} icon={GraduationCap} />
              <SliderRow testid="sim-scholarships" label="Scholarships + grants" value={(sc.scholarships || 0)} min={0} max={200000} step={2500} onChange={(v) => set({ scholarships: v })} fmt={fmtMoney} icon={Award} />
              {sc.housing === 'own' && <SliderRow testid="sim-home-price" label="Home price" value={sc.home_price} min={100000} max={1500000} step={10000} onChange={(v) => set({ home_price: v })} fmt={fmtMoney} icon={Home} />}
              <SliderRow testid="sim-savings-rate" label="Savings rate" value={sc.savings_rate} min={0} max={40} step={1} onChange={(v) => set({ savings_rate: v })} fmt={(v) => `${v}%`} icon={PiggyBank} />
              <SliderRow testid="sim-investment-pct" label="% of savings invested" value={sc.investment_pct} min={0} max={100} step={5} onChange={(v) => set({ investment_pct: v })} fmt={(v) => `${v}%`} icon={TrendingUp} />
              <SliderRow testid="sim-side-income" label="Side business income" value={sc.side_income} min={0} max={200000} step={5000} onChange={(v) => set({ side_income: v })} fmt={fmtMoney} icon={Zap} />
              <SliderRow testid="sim-children" label="Children" value={sc.kids} min={0} max={5} step={1} onChange={(v) => set({ kids: v })} fmt={(v) => `${v}`} icon={Home} />
              <SliderRow testid="sim-retirement-age" label={`${savingLabel === 'Retirement' ? 'Retirement' : 'Stop-working'} age`} value={sc.retirement_age} min={45} max={75} step={1} onChange={(v) => set({ retirement_age: v })} fmt={(v) => `${v}`} icon={Rocket} />

              {/* Decision impact presets */}
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-300 mb-2">Try a decision</p>
                <div className="flex flex-wrap gap-2">
                  {presets.map((p) => (
                    <button key={p.label} onClick={p.apply} data-testid={`preset-${p.label.replace(/[^a-z]/gi,'-').toLowerCase()}`}
                      className="text-[11px] px-2.5 py-1.5 rounded-full bg-slate-800/70 border border-slate-700 text-slate-200 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors">
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </Glow>

            {/* Right column: insights + coach + lifetime breakdown */}
            <div className="space-y-6">
              {/* Contextual financial intelligence */}
              <Glow testid="financial-intelligence" glow="rgba(45,224,168,0.12)">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-200 mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Financial Intelligence</h2>
                <div className="space-y-2.5">
                  {insights.length === 0 && <p className="text-xs text-slate-300">Your plan looks balanced. Adjust the sliders to explore tradeoffs.</p>}
                  {insights.map((ins, i) => (
                    <div key={i} className={`flex items-start gap-2 text-xs rounded-lg p-2.5 border ${ins.tone === 'warn' ? 'border-amber-400/25 bg-amber-500/8 text-amber-100' : 'border-emerald-400/25 bg-emerald-500/8 text-emerald-100'}`}>
                      {ins.tone === 'warn' ? <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />}
                      <span>{ins.text}</span>
                    </div>
                  ))}
                </div>
              </Glow>

              {/* AI Coach */}
              <Glow testid="ai-coach" glow="rgba(124,58,237,0.16)">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-violet-200 flex items-center gap-2"><Brain className="w-4 h-4" /> FuturePath Coach</h2>
                  <Button size="sm" onClick={runCoach} disabled={coaching} className="rounded-full bg-violet-600 hover:bg-violet-500 text-white h-8" data-testid="coach-btn">
                    <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${coaching ? 'animate-pulse' : ''}`} /> {coaching ? 'Thinking…' : 'Get coaching'}
                  </Button>
                </div>
                <AnimatePresence mode="wait">
                  {coach ? (
                    <motion.div key="c" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
                      <p className="text-white font-semibold text-sm">{coach.headline}</p>
                      <p className="text-slate-300 text-xs leading-relaxed">{coach.coaching}</p>
                      <ul className="space-y-1.5 mt-2">
                        {(coach.recommendations || []).map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-200"><Rocket className="w-3.5 h-3.5 text-violet-300 flex-shrink-0 mt-0.5" /> {r}</li>
                        ))}
                      </ul>
                    </motion.div>
                  ) : (
                    <p key="e" className="text-xs text-slate-300">Tap “Get coaching” for personalized guidance on your current plan and higher-ceiling paths to your goal.</p>
                  )}
                </AnimatePresence>
              </Glow>

              {/* Lifetime breakdown */}
              <Glow testid="lifetime-projection" glow="rgba(23,204,244,0.12)">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-cyan-200 mb-3 flex items-center gap-2"><Layers className="w-4 h-4" /> Lifetime Income Projection</h2>
                <div className="grid grid-cols-2 gap-2">
                  <Stat label="Total Career Earnings" value={fmtMoney(summary.lifetimeEarnings)} />
                  <Stat label="Taxes Paid" value={fmtMoney(summary.totalTaxes)} accent="text-rose-300" />
                  <Stat label="Retirement + Match" value={fmtMoney(summary.retirementContributions + summary.employerMatch)} accent="text-emerald-300" />
                  <Stat label="Peak Net Worth" value={fmtMoney(summary.peakNetWorth)} accent="text-violet-300" />
                </div>
              </Glow>
            </div>
          </div>

          {/* Scenario Comparison */}
          <Glow testid="scenario-comparison" glow="rgba(124,58,237,0.12)">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-violet-200 flex items-center gap-2"><Layers className="w-4 h-4" /> Compare Futures</h2>
              <Button size="sm" onClick={saveScenario} variant="outline" className="rounded-full border-violet-400/40 text-violet-200 hover:bg-violet-500/10 h-8" data-testid="save-scenario-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Save current
              </Button>
            </div>
            {saved.length === 0 ? (
              <p className="text-xs text-slate-300">Save two or three versions of your plan (e.g. a different career, state, or education path) to see them side by side. They're saved to your account. Click one anytime to reload it into the simulator.</p>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3">
                {saved.map((s) => (
                  <div key={s.id} role="button" tabIndex={0} onClick={() => openScenario(s)} onKeyDown={(e) => e.key === 'Enter' && openScenario(s)}
                    className="relative cursor-pointer rounded-xl border border-violet-400/20 bg-slate-900/50 p-4 hover:border-cyan-400/40 hover:bg-slate-900/70 transition-colors" data-testid={`scenario-card-${s.name}`}>
                    <button data-testid={`scenario-remove-${s.name}`} aria-label={`Remove ${s.name}`} onClick={(e) => { e.stopPropagation(); removeScenario(s.id); }} className="absolute top-2 right-2 text-slate-300 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                    <p className="text-violet-200 text-xs font-semibold uppercase tracking-wide">{s.name} <span className="text-cyan-300/70 normal-case font-normal">· tap to open</span></p>
                    <p className="text-white text-sm font-bold mt-1 leading-tight">{s.career}</p>
                    <p className="text-[11px] text-slate-300">{s.pathway} · {s.state} · retire {s.retire}</p>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex justify-between text-xs"><span className="text-slate-300">Lifetime</span><span className="text-cyan-200 font-semibold">{fmtMoney(s.lifetime)}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-300">Net worth @ retire</span><span className="text-emerald-200 font-semibold">{fmtMoney(s.netWorth)}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-300">FI age</span><span className="text-violet-200 font-semibold">{s.fiAge || '-'}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Glow>

          <p className="text-center text-slate-300 text-[11px] flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3" /> Projections are estimates for exploration, not financial advice. They update as you refine your FuturePath journey.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
